/**
 * Hráči, tímy a vypadnutie. Odtiaľto berie zvyšok enginu odpoveď na otázku
 * „je toto spojenec alebo súper" — nikde inde sa `owner` neporovnáva priamo.
 */

import type { Fighter, GameState, PlayerIndex, PlayerState } from './types.js';

export function playerOf(state: GameState, index: PlayerIndex): PlayerState | undefined {
  return state.players[index];
}

export function teamOf(state: GameState, index: PlayerIndex): number {
  return state.players[index]?.team ?? index;
}

/** Spoluhráč aj ty sám. V duele a FFA je spojencom len hráč sám sebe. */
export function areAllies(state: GameState, a: PlayerIndex, b: PlayerIndex): boolean {
  return teamOf(state, a) === teamOf(state, b);
}

export function areEnemies(state: GameState, a: PlayerIndex, b: PlayerIndex): boolean {
  return !areAllies(state, a, b);
}

export function fightersOfTeam(state: GameState, team: number): Fighter[] {
  return state.fighters.filter((f) => teamOf(state, f.owner) === team);
}

export function teams(state: GameState): number[] {
  return [...new Set(state.players.map((p) => p.team))].sort((a, b) => a - b);
}

export function hasLivingHero(state: GameState, team: number): boolean {
  return state.fighters.some((f) => f.kind === 'hero' && teamOf(state, f.owner) === team);
}

/** Hráči jedného tímu, ktorí ešte nevypadli. */
export function activePlayersOfTeam(state: GameState, team: number): PlayerState[] {
  return state.players.filter((p) => p.team === team && !p.eliminated);
}

/**
 * Ďalší hráč na ťahu — preskočí vypadnutých.
 * Poradie je jednoducho poradie slotov; v 2v2 sú sloty rozdané striedavo
 * (A1, B1, A2, B2), takže sa tímy striedajú samy od seba.
 */
export function nextPlayer(state: GameState, from: PlayerIndex): PlayerIndex {
  const count = state.players.length;
  for (let step = 1; step <= count; step++) {
    const candidate = (from + step) % count;
    if (!state.players[candidate]?.eliminated) return candidate;
  }
  return from;
}

/**
 * Vo free-for-all smie hráč v SVOJOM PRVOM ťahu zaútočiť len na suseda v poradí
 * alebo na niekoho, kto už ťah mal. Bráni to tomu, aby prvý hráč zbil niekoho,
 * kto sa ešte nemohol pohnúť. (Core Rules, „Free-for-all", bod 2)
 */
export function canTargetInFirstRound(
  state: GameState,
  attacker: PlayerIndex,
  target: PlayerIndex,
): boolean {
  if (state.format !== 'ffa') return true;
  const count = state.players.length;
  // Prvé kolo trvá, kým každý neodohral jeden ťah.
  if (state.turn.number > count) return true;
  // Kto už ťah mal, má nižšie číslo slotu než hráč na ťahu v prvom kole.
  if (target < attacker) return true;
  return target === (attacker + 1) % count;
}
